import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Patient } from '../types/patient'
import { immunisationService } from '../services/immunisationService'
import { calculatePreventiveServices } from '../services/preventiveCareService'
import { PreventiveService } from '../types/preventiveCare'
import './PreventiveServicesAlerts.css'

interface PreventiveServicesAlertsProps {
  patient: Patient
  /** When true, influenza and related rules use pregnancy as a high-priority risk factor */
  hasActivePregnancy?: boolean
  onServiceClick?: (service: PreventiveService) => void
}

/**
 * Component that displays alerts for all patient-specific preventive services that are due.
 * 
 * Includes:
 * - Smoking status assessment
 * - Immunization recommendations (pneumococcal, influenza, HPV)
 * - Gender- and age-based screening (e.g. cervical screening)
 * - Other preventive services based on demographics and risk factors
 */
export function PreventiveServicesAlerts({
  patient,
  hasActivePregnancy = false,
  onServiceClick,
}: PreventiveServicesAlertsProps) {
  const navigate = useNavigate()

  // Fetch patient immunizations
  const { data: immunisationsData } = useQuery({
    queryKey: ['patient-immunisations', patient.id],
    queryFn: () => immunisationService.getPatientImmunisations(patient.id),
    enabled: !!patient.id,
  })

  const preventiveServices = useMemo(() => {
    const immunisations = immunisationsData?.items || []
    return calculatePreventiveServices({
      patient,
      immunisations,
      hasActivePregnancy,
    })
  }, [patient, immunisationsData, hasActivePregnancy])

  // Don't render if no due services
  if (!preventiveServices || preventiveServices.dueCount === 0) {
    return null
  }

  const handleServiceClick = (service: PreventiveService) => {
    if (onServiceClick) {
      onServiceClick(service)
    } else if (service.actionUrl) {
      // If actionUrl is a hash (starts with #), it's handled by onServiceClick
      // Otherwise, navigate to the URL
      if (!service.actionUrl.startsWith('#')) {
        navigate(service.actionUrl)
      }
    }
  }

  // Sort services: overdue first, then by priority (high, medium, low)
  const sortedServices = [...preventiveServices.dueServices].sort((a, b) => {
    // Overdue first
    if (a.status === 'overdue' && b.status !== 'overdue') return -1
    if (a.status !== 'overdue' && b.status === 'overdue') return 1

    // Then by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 }
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
    if (priorityDiff !== 0) return priorityDiff

    // Then alphabetically by name
    return a.name.localeCompare(b.name)
  })

  return (
    <div className="preventive-services-alerts" role="alert" aria-live="polite">
      <div className="preventive-services-alerts-header">
        <div className="preventive-services-alerts-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9V11M12 15H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0377 2.66667 10.2679 4L3.33975 16C2.56995 17.3333 3.53223 19 5.07183 19Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="preventive-services-alerts-title">
          Preventive Services Due ({preventiveServices.dueCount}
          {preventiveServices.overdueCount > 0 && (
            <span className="preventive-services-alerts-overdue-count">
              {' '}
              - {preventiveServices.overdueCount} overdue
            </span>
          )}
          )
        </div>
      </div>
      <div className="preventive-services-alerts-list">
        {sortedServices.map((service) => (
          <PreventiveServiceAlertItem
            key={service.id}
            service={service}
            onClick={() => handleServiceClick(service)}
          />
        ))}
      </div>
    </div>
  )
}

interface PreventiveServiceAlertItemProps {
  service: PreventiveService
  onClick: () => void
}

function PreventiveServiceAlertItem({
  service,
  onClick,
}: PreventiveServiceAlertItemProps) {
  const getStatusClass = (status: PreventiveService['status']) => {
    switch (status) {
      case 'overdue':
        return 'preventive-service-overdue'
      case 'due':
        return 'preventive-service-due'
      default:
        return ''
    }
  }

  const getPriorityClass = (priority: PreventiveService['priority']) => {
    switch (priority) {
      case 'high':
        return 'preventive-service-priority-high'
      case 'medium':
        return 'preventive-service-priority-medium'
      case 'low':
        return 'preventive-service-priority-low'
      default:
        return ''
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  return (
    <div
      className={`preventive-service-alert-item ${getStatusClass(service.status)} ${getPriorityClass(service.priority)}`}
    >
      <div className="preventive-service-alert-content">
        <div className="preventive-service-alert-header">
          <h4 className="preventive-service-alert-name">{service.name}</h4>
          {service.status === 'overdue' && (
            <span className="preventive-service-alert-badge overdue">
              Overdue
            </span>
          )}
          {service.priority === 'high' && (
            <span className="preventive-service-alert-badge priority">
              High Priority
            </span>
          )}
        </div>
        <p className="preventive-service-alert-description">
          {service.description}
        </p>
        <div className="preventive-service-alert-reason">
          <strong>Reason:</strong> {service.reason}
        </div>
        {service.dueDate && (
          <div className="preventive-service-alert-date">
            <strong>Due:</strong> {formatDate(service.dueDate)}
          </div>
        )}
        {service.metadata?.daysSinceLastVaccination !== undefined &&
          service.metadata.daysSinceLastVaccination > 0 && (
            <div className="preventive-service-alert-metadata">
              Last vaccination: {Math.floor(service.metadata.daysSinceLastVaccination / 30)}{' '}
              months ago
            </div>
          )}
      </div>
      {service.actionLabel && (
        <button
          className="preventive-service-alert-button"
          onClick={onClick}
          type="button"
          aria-label={`${service.actionLabel}: ${service.name}`}
        >
          {service.actionLabel}
        </button>
      )}
    </div>
  )
}
